import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CalendarIcon, CrownIcon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SessionFeedbackForm from "@/components/profile/SessionFeedbackForm";
import SessionFeedbackDisplay from "@/components/profile/SessionFeedbackDisplay";

type Session = {
  id: string;
  session_type: string;
  company: string | null;
  level: string | null;
  scheduled_at: string | null;
  notes: string | null;
  created_at: string;
  feedback_submitted: boolean | null;
};

type Feedback = {
  id: string;
  rating: number;
  feedback_text: string | null;
  created_at: string;
  session_id: string;
};

const Profile = () => {
  const { user, isLoading, isSubscribed, isPremiumLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [isSessionsLoading, setIsSessionsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<{start: string | null, end: string | null}>({
    start: null,
    end: null
  });

  // Subscription management state
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelledAtPeriodEnd, setIsCancelledAtPeriodEnd] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const CANCELLATION_REASONS = [
    { value: "not_enough_content", label: "I couldn't find the topics I was looking for" },
    { value: "content_quality", label: "The content didn't meet my expectations" },
    { value: "no_value", label: "It wasn't worth the price for me" },
    { value: "found_alternative", label: "I'm using a different resource now" },
    { value: "other", label: "Something else" },
  ];
  
  useEffect(() => {
    // Redirect if not logged in
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    // Fetch subscription data
    const fetchSubscriptionInfo = async () => {
      if (!user || !isSubscribed) return;
      
      try {
        const { data, error } = await supabase
          .from('subscribers')
          .select('subscription_start, subscription_end')
          .eq('user_id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setSubscriptionData({
            start: data.subscription_start,
            end: data.subscription_end
          });
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      }
    };
    
    fetchSubscriptionInfo();
  }, [user, isSubscribed]);

  useEffect(() => {
    const fetchSessionsAndFeedback = async () => {
      if (user) {
        try {
          setIsSessionsLoading(true);
          
          // Fetch sessions
          const { data: sessionsData, error: sessionsError } = await supabase
            .from('candidate_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (sessionsError) throw sessionsError;
          
          // Fetch feedback
          const { data: feedbackData, error: feedbackError } = await supabase
            .from('session_feedback')
            .select('*')
            .eq('user_id', user.id);
          
          if (feedbackError) throw feedbackError;
          
          setSessions(sessionsData || []);
          setFeedbacks(feedbackData || []);
        } catch (error) {
          console.error('Error fetching sessions and feedback:', error);
        } finally {
          setIsSessionsLoading(false);
        }
      }
    };
    
    fetchSessionsAndFeedback();
  }, [user]);

  const handleFeedbackSubmitted = () => {
    // Refresh sessions and feedback data
    const refreshData = async () => {
      if (user) {
        try {
          const { data: sessionsData, error: sessionsError } = await supabase
            .from('candidate_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (sessionsError) throw sessionsError;
          
          const { data: feedbackData, error: feedbackError } = await supabase
            .from('session_feedback')
            .select('*')
            .eq('user_id', user.id);
          
          if (feedbackError) throw feedbackError;
          
          setSessions(sessionsData || []);
          setFeedbacks(feedbackData || []);
        } catch (error) {
          console.error('Error refreshing data:', error);
        }
      }
    };
    
    refreshData();
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: { immediate: false },
      });
      if (error) throw error;
      toast({
        title: "Subscription cancelled",
        description: data.message,
      });
      setIsCancelledAtPeriodEnd(true);
      setShowFeedbackDialog(true);
      // Refresh subscription data
      const { data: refreshed } = await supabase.functions.invoke("check-subscription");
      if (refreshed) {
        setSubscriptionData({
          start: refreshed.subscription_start || null,
          end: refreshed.subscription_end || null,
        });
      }
    } catch (err: any) {
      toast({
        title: "Error cancelling subscription",
        description: err.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmitFeedback = async () => {
    setIsSubmittingFeedback(true);
    try {
      await supabase.from("page_events").insert({
        event_type: "subscription_cancellation_feedback",
        page_path: "/profile",
        user_id: user?.id,
        user_email: user?.email,
        extra_data: {
          reason: feedbackReason,
          details: feedbackDetails,
        },
      });
      toast({
        title: "Thank you for your feedback",
        description: "Your feedback helps us improve HelloML.",
      });
    } catch {
      // Silently fail - feedback is optional
    } finally {
      setIsSubmittingFeedback(false);
      setShowFeedbackDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <div className="container max-w-5xl py-12">
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  // Format session type for display
  const formatSessionType = (type: string) => {
    if (type === "mock_interview") return "Mock Interview";
    if (type === "coaching") return "1:1 Coaching";
    return type;
  };

  const getFeedbackForSession = (sessionId: string) => {
    return feedbacks.find(feedback => feedback.session_id === sessionId);
  };

  const canSubmitFeedback = (session: Session) => {
    // Can't submit feedback if already submitted
    if (session.feedback_submitted) return false;
    
    // If no scheduled_at, assume it's a completed session that can receive feedback
    if (!session.scheduled_at) return true;
    
    const sessionDate = new Date(session.scheduled_at);
    const now = new Date();
    
    // Session should be completed (in the past) to allow feedback
    return sessionDate < now;
  };

  const getSessionStatus = (session: Session) => {
    const feedback = getFeedbackForSession(session.id);
    
    if (feedback) {
      return { text: "Feedback Submitted", color: "text-green-600" };
    }
    
    if (!session.scheduled_at) {
      // If no scheduled_at but can submit feedback, it's likely a completed session
      if (canSubmitFeedback(session)) {
        return { text: "Feedback Pending", color: "text-blue-600" };
      } else {
        return { text: "Completed", color: "text-green-600" };
      }
    }
    
    const sessionDate = new Date(session.scheduled_at);
    const now = new Date();
    
    if (sessionDate < now) {
      // Session is in the past
      if (canSubmitFeedback(session)) {
        return { text: "Feedback Pending", color: "text-blue-600" };
      } else {
        return { text: "Completed", color: "text-green-600" };
      }
    } else {
      // Session is in the future
      return { text: "Scheduled", color: "text-gray-500" };
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="container max-w-5xl py-12">
          <h1 className="text-3xl font-bold mb-8">My Profile</h1>

          <Tabs defaultValue="sessions" className="w-full">
            <TabsList>
              <TabsTrigger value="sessions">My Sessions</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="account">Account Info</TabsTrigger>
            </TabsList>
        
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle>My Scheduled Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {isSessionsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : sessions.length > 0 ? (
                <div className="space-y-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Session Type</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Level</TableHead>
                        <TableHead>Scheduled For</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => {
                        const status = getSessionStatus(session);
                        
                        return (
                          <TableRow key={session.id}>
                            <TableCell>{formatSessionType(session.session_type)}</TableCell>
                            <TableCell>{session.company || "N/A"}</TableCell>
                            <TableCell>{session.level || "N/A"}</TableCell>
                            <TableCell>
                              {session.scheduled_at 
                                ? format(new Date(session.scheduled_at), "MMM d, yyyy h:mm a") 
                                : "Not scheduled yet"}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">
                              {session.notes || "No notes"}
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${status.color}`}>
                                {status.text}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  
                  {/* Feedback Forms and Displays */}
                  {sessions.map((session) => {
                    const feedback = getFeedbackForSession(session.id);
                    const canSubmit = canSubmitFeedback(session);
                    
                    if (feedback) {
                      return (
                        <div key={`feedback-${session.id}`}>
                          <h3 className="text-lg font-medium mb-2">
                            {formatSessionType(session.session_type)} - {session.scheduled_at ? format(new Date(session.scheduled_at), "MMM d, yyyy") : "TBD"}
                          </h3>
                          <SessionFeedbackDisplay 
                            feedback={feedback} 
                            sessionType={session.session_type}
                          />
                        </div>
                      );
                    }
                    
                    if (canSubmit) {
                      return (
                        <div key={`form-${session.id}`}>
                          <h3 className="text-lg font-medium mb-2">
                            {formatSessionType(session.session_type)} - {session.scheduled_at ? format(new Date(session.scheduled_at), "MMM d, yyyy") : "TBD"}
                          </h3>
                          <SessionFeedbackForm
                            sessionId={session.id}
                            sessionType={session.session_type}
                            onFeedbackSubmitted={handleFeedbackSubmitted}
                          />
                        </div>
                      );
                    }
                    
                    return null;
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">You haven't scheduled any sessions yet.</p>
                  <div className="flex justify-center gap-4">
                    <Button onClick={() => navigate('/mock-interview')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Schedule Mock Interview
                    </Button>
                    <Button onClick={() => navigate('/coaching')} variant="outline">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Book 1:1 Coaching
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
              <CardDescription>Manage your HelloML Premium subscription</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isPremiumLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-muted-foreground">Loading subscription details...</span>
                </div>
              ) : isSubscribed && !isCancelledAtPeriodEnd ? (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Status</span>
                      <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Active</span>
                    </div>
                    {subscriptionData.start && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Started</span>
                        <span>{format(new Date(subscriptionData.start), "MMM d, yyyy")}</span>
                      </div>
                    )}
                    {subscriptionData.end && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Expires</span>
                        <span>{format(new Date(subscriptionData.end), "MMM d, yyyy")}</span>
                      </div>
                    )}
                  </div>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isCancelling}>
                        {isCancelling ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cancelling...</>
                        ) : (
                          "Cancel Subscription"
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-destructive" />
                          Cancel Subscription
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Your subscription will remain active until the end of the current billing period. <strong>You will not be charged again.</strong> After that, you'll lose access to premium content. Are you sure you want to cancel?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleCancelSubscription}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Yes, Cancel
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : isCancelledAtPeriodEnd ? (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Status</span>
                      <span className="text-sm font-medium text-destructive bg-destructive/10 px-2 py-1 rounded">Cancelled</span>
                    </div>
                    {subscriptionData.end && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Access until</span>
                        <span>{format(new Date(subscriptionData.end), "MMM d, yyyy")}</span>
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-destructive" />
                      <span className="font-medium text-destructive">Subscription Cancelled</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      You will <strong>not be billed again</strong>. Your access to premium content will remain active until{' '}
                      {subscriptionData.end
                        ? format(new Date(subscriptionData.end), "MMMM d, yyyy")
                        : 'the end of your billing period'}
                      .
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 space-y-3">
                  <p className="text-muted-foreground">You don't have an active subscription.</p>
                  <Button onClick={() => navigate('/get-premium')}>Get Premium</Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cancellation Feedback Dialog */}
          <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>We're sorry to see you go</DialogTitle>
                <DialogDescription>
                  Your subscription has been cancelled and you will not be billed again. Could you help us improve by sharing why you're leaving?
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <RadioGroup value={feedbackReason} onValueChange={setFeedbackReason}>
                  {CANCELLATION_REASONS.map((reason) => (
                    <div key={reason.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={reason.value} id={`profile-${reason.value}`} />
                      <Label htmlFor={`profile-${reason.value}`} className="cursor-pointer">{reason.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
                {feedbackReason === "other" && (
                  <Textarea
                    placeholder="Tell us more..."
                    value={feedbackDetails}
                    onChange={(e) => setFeedbackDetails(e.target.value)}
                    className="resize-none"
                    rows={3}
                  />
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="ghost" onClick={() => setShowFeedbackDialog(false)}>
                  Skip
                </Button>
                <Button onClick={handleSubmitFeedback} disabled={!feedbackReason || isSubmittingFeedback}>
                  {isSubmittingFeedback ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                  ) : (
                    "Submit Feedback"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-muted-foreground">{user?.email}</p>
                  </div>
                  {user?.user_metadata?.name && (
                    <div>
                      <p className="font-medium">Name</p>
                      <p className="text-muted-foreground">{user.user_metadata.name}</p>
                    </div>
                  )}
                </div>
                
                {/* Premium Membership Section */}
                <div className="border-t pt-5">
                  <h3 className="font-semibold text-lg mb-4">Membership Status</h3>
                  
                  {isPremiumLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking membership status...</span>
                    </div>
                  ) : isSubscribed ? (
                    <div className="bg-primary/10 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-primary">
                        <CrownIcon className="h-5 w-5" />
                        <span className="font-medium">Premium Member</span>
                      </div>
                      
                      {subscriptionData.end && (
                        <div>
                          <p className="text-sm text-muted-foreground">Your premium membership expires on:</p>
                          <p className="font-medium">
                            {format(new Date(subscriptionData.end), "MMMM d, yyyy")}
                          </p>
                        </div>
                      )}
                      
                      {subscriptionData.start && (
                        <div className="text-sm text-muted-foreground">
                          <p>Member since: {format(new Date(subscriptionData.start), "MMMM d, yyyy")}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <p>You are currently using a free account.</p>
                      <Button 
                        onClick={() => navigate('/pricing')} 
                        variant="outline" 
                        className="w-fit"
                      >
                        <CrownIcon className="mr-2 h-4 w-4" />
                        Upgrade to Premium
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
